import type { FeatureCollection } from 'geojson';
import { sumBy } from 'lodash-es';
import {
  type Region,
  RegionList,
  Regions
} from 'maestro-shared/referential/Region';
import {
  getCompletionRate,
  type LocalPrescription
} from 'maestro-shared/schema/LocalPrescription/LocalPrescription';
import type { ProgrammingPlanContext } from 'maestro-shared/schema/ProgrammingPlan/Context';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import type {
  CircleLayerSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
  Point,
  StyleSpecification,
  SymbolLayerSpecification
} from 'maplibre-gl';
import { useContext, useMemo, useRef, useState } from 'react';
import { Layer, Map as MapLibre, Source } from 'react-map-gl/maplibre';
import { useNavigate } from 'react-router';
import { AuthenticatedAppRoutes } from '../../AppRoutes';
import { ApiClientContext } from '../../services/apiClient';
import { maplibregl } from '../../utils/maplibre';

interface Props {
  programmingPlan: ProgrammingPlanChecked;
  context: ProgrammingPlanContext;
  localPrescriptions: LocalPrescription[];
}

const ProgrammingPlanMap = ({
  programmingPlan,
  context,
  localPrescriptions
}: Props) => {
  const apiClient = useContext(ApiClientContext);
  const ref = useRef<any>(undefined);
  const navigate = useNavigate();

  const [hoverInfo, setHoverInfo] = useState<{
    feature: MapGeoJSONFeature;
    position: Point;
  }>();

  const { data: regions } = apiClient.useGetRegionsGeoJsonQuery();

  const mapStyle: StyleSpecification = useMemo(
    () => ({
      version: 8,
      sources: {
        openmaptiles: {
          type: 'vector',
          url: 'https://openmaptiles.geo.data.gouv.fr/data/france-vector.json'
        }
      },
      glyphs:
        'https://openmaptiles.geo.data.gouv.fr/fonts/{fontstack}/{range}.pbf',
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: { 'background-color': '#f8f4f0' }
        }
      ]
    }),
    []
  );

  const hoveredRegion = useMemo(() => {
    return String(hoverInfo?.feature.id).padStart(2, '0') as Region;
  }, [hoverInfo]);

  if (!regions || !localPrescriptions) {
    return null;
  }

  const getSampleCount = (region: Region) =>
    sumBy(
      localPrescriptions.filter(
        (regionalPrescription) => regionalPrescription.region === region
      ),
      'sampleCount'
    );

  const getRealizedSampleCount = (region: Region) =>
    sumBy(
      localPrescriptions.filter(
        (regionalPrescription) => regionalPrescription.region === region
      ),
      'realizedSampleCount'
    );

  const addRegionProperties = (featureCollection: any) => ({
    ...featureCollection,
    features: featureCollection.features.map((feature: any) => {
      const region = feature.id as Region;

      return region
        ? {
            ...feature,
            properties: {
              ...feature.properties,
              title: Regions[region].name,
              sampleCount: getSampleCount(region),
              realizedSampleCount: getRealizedSampleCount(region),
              completionRate: getCompletionRate(localPrescriptions, region)
            }
          }
        : feature;
    })
  });

  const regionCenters: FeatureCollection = addRegionProperties({
    type: 'FeatureCollection',
    features: RegionList.map((region) => ({
      type: 'Feature',
      id: region,
      geometry: {
        type: 'Point',
        coordinates: [
          Regions[region].center.longitude,
          Regions[region].center.latitude
        ]
      },
      properties: {
        id: region
      }
    }))
  });

  const centerCircleLayer: CircleLayerSpecification = {
    id: 'center-circle',
    type: 'circle',
    source: 'centers',
    filter: ['>', ['get', 'sampleCount'], 0],
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['get', 'sampleCount'],
        10,
        10,
        100,
        20
      ],
      'circle-color': '#000091'
    }
  };

  const centerCountLayer: SymbolLayerSpecification = {
    id: 'center-count',
    type: 'symbol',
    source: 'centers',
    filter: ['>', ['get', 'sampleCount'], 0],
    layout: {
      'text-field': ['get', 'sampleCount'],
      'text-size': 12
    },
    paint: {
      'text-color': '#ffffff',
      'text-opacity': 1
    }
  };

  const regionsFillLayer: FillLayerSpecification = {
    id: 'regions-fill',
    type: 'fill',
    source: 'mapbox',
    paint: {
      'fill-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#000091',
        ['==', ['get', 'sampleCount'], 0],
        '#f9f6f2',
        '#6a6af4'
      ],
      'fill-opacity': [
        'case',
        ['==', ['get', 'sampleCount'], 0],
        0.4,
        ['boolean', ['feature-state', 'hover'], false],
        1,
        ['interpolate', ['linear'], ['get', 'completionRate'], 5, 0.1, 95, 1]
      ]
    }
  };

  const regionsLineLayer: LineLayerSpecification = {
    id: 'regions-line',
    type: 'line',
    source: 'mapbox',
    paint: {
      'line-color': '#000091',
      'line-width': 0.1
    }
  };

  const onHover = (e: MapLayerMouseEvent) => {
    if (hoverInfo?.feature) {
      ref.current.setFeatureState(
        { source: 'regions', id: hoverInfo.feature.id },
        { hover: false }
      );
      ref.current.setFeatureState(
        { source: 'centers', id: hoverInfo.feature.id },
        { hover: false }
      );
    }
    if (e.features && e.features.length > 0) {
      setHoverInfo({
        feature: e.features[0],
        position: e.point
      });
      ref.current.setFeatureState(
        { source: 'regions', id: e.features[0].id },
        { hover: true }
      );
      ref.current.setFeatureState(
        { source: 'centers', id: e.features[0].id },
        { hover: true }
      );
    } else {
      setHoverInfo(undefined);
    }
  };

  const onClick = (e: MapLayerMouseEvent) => {
    if (e.features && e.features.length > 0) {
      navigate(
        AuthenticatedAppRoutes.SamplesByYearRoute.link(programmingPlan.year, {
          programmingPlanIds: [programmingPlan.id],
          contexts: [context],
          regions: [hoveredRegion]
        })
      );
    }
  };

  return (
    <div data-testid="prescription-map">
      <MapLibre
        ref={ref}
        attributionControl={{}}
        id="prescriptionMap"
        initialViewState={{
          latitude: 46,
          longitude: 2,
          zoom: 4
        }}
        scrollZoom={false}
        dragPan={false}
        style={{
          minHeight: 400,
          fontFamily: 'Marianne, sans-serif'
        }}
        mapLib={maplibregl}
        mapStyle={mapStyle}
        interactiveLayerIds={['regions-fill']}
        onMouseMove={onHover}
        onClick={onClick}
        cursor="pointer"
      >
        {regions && (
          <>
            <Source
              id="regions"
              type="geojson"
              data={addRegionProperties(regions)}
            >
              <Layer {...regionsFillLayer} />
              <Layer {...regionsLineLayer} />
            </Source>
            <Source id="centers" type="geojson" data={regionCenters}>
              <Layer {...centerCircleLayer} />
              <Layer {...centerCountLayer} />
            </Source>
          </>
        )}
        {hoverInfo?.feature && (
          <div
            className="fr-tooltip fr-placement fr-tooltip--shown"
            style={{
              left: hoverInfo.position.x,
              top: hoverInfo.position.y,
              position: 'absolute',
              margin: 8,
              padding: 4,
              background: 'white',
              pointerEvents: 'none'
            }}
          >
            <div>{Regions[hoveredRegion].name}</div>
            <div>{getSampleCount(hoveredRegion)} prélevements</div>
            <div>{getRealizedSampleCount(hoveredRegion)} réalisés</div>
            <div>
              Taux de réalisation :{' '}
              {getCompletionRate(localPrescriptions, hoveredRegion)}%
            </div>
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            bottom: 5,
            left: 15
          }}
        >
          Taux de réalisation
          <div
            style={{
              height: 15,
              width: 110,
              color: 'white',
              background: 'linear-gradient(90deg, #ebe7f0, #6a6af4)'
            }}
          ></div>
          <span>1</span>
          <span style={{ float: 'right' }}>100</span>
        </div>
      </MapLibre>
    </div>
  );
};

export default ProgrammingPlanMap;
